import { useState } from 'react';
import { Bell, BellOff, TestTube, AlertCircle, CheckCircle, Loader } from 'lucide-react';
import { useNotifications } from '../hooks/useNotifications';
import { LoadingSpinner } from './LoadingSpinner';

export const NotificationSettings = () => {
  const {
    isSupported,
    permission,
    fcmToken,
    isRegistering,
    error,
    requestPermission,
    unregister,
    sendTestNotification
  } = useNotifications();

  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<string | null>(null);

  const handleEnableNotifications = async () => {
    const success = await requestPermission();
    if (success) {
      setTestResult('Notifications enabled successfully!');
    }
  };

  const handleDisableNotifications = async () => {
    const success = await unregister();
    if (success) {
      setTestResult('Notifications disabled successfully!');
    }
  };

  const handleTestNotification = async () => {
    setIsTesting(true);
    setTestResult(null);

    try {
      const result = await sendTestNotification();
      
      if (result.success) {
        setTestResult('Test notification sent successfully!');
      } else {
        setTestResult(`Test failed: ${result.error}`);
      }
    } catch (error) {
      setTestResult(`Test failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsTesting(false);
    }
  };

  const getPermissionStatus = () => {
    switch (permission) {
      case 'granted':
        return { text: 'Enabled', color: 'text-status-success', bg: 'bg-status-success/10' };
      case 'denied':
        return { text: 'Blocked', color: 'text-status-error', bg: 'bg-status-error/10' };
      default:
        return { text: 'Not Set', color: 'text-status-warning', bg: 'bg-status-warning/10' };
    }
  };

  const status = getPermissionStatus();

  if (!isSupported) {
    return (
      <div className="card p-6">
        <div className="flex items-center space-x-3 mb-4">
          <BellOff className="w-6 h-6 text-th-secondary" />
          <h3 className="text-lg font-semibold text-th-primary">Push Notifications</h3>
        </div>
        <div className="p-4 bg-status-error/10 border border-status-error/30 rounded-lg">
          <div className="flex items-center space-x-2">
            <AlertCircle className="w-5 h-5 text-status-error" />
            <span className="text-status-error">Notifications not supported in this browser</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Bell className="w-6 h-6 text-brand" />
          <div>
            <h3 className="text-lg font-semibold text-th-primary">Push Notifications</h3>
            <p className="text-sm text-th-secondary">Get notified when your content is ready</p>
          </div>
        </div>
        <div className={`px-3 py-1 rounded-full text-sm font-medium ${status.bg} ${status.color}`}>
          {status.text}
        </div>
      </div>

      {/* Current Status */}
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-elevated border border-subtle rounded-lg p-4">
            <h4 className="text-th-primary font-medium mb-2">Browser Support</h4>
            <div className="flex items-center space-x-2">
              <div className={`w-2 h-2 rounded-full ${isSupported ? 'bg-status-success' : 'bg-status-error'}`} />
              <span className="text-th-secondary text-sm">
                {isSupported ? 'Supported' : 'Not Supported'}
              </span>
            </div>
          </div>

          <div className="bg-elevated border border-subtle rounded-lg p-4">
            <h4 className="text-th-primary font-medium mb-2">Permission Status</h4>
            <div className="flex items-center space-x-2">
              <div className={`w-2 h-2 rounded-full ${
                permission === 'granted' ? 'bg-status-success' : 
                permission === 'denied' ? 'bg-status-error' : 'bg-status-warning'
              }`} />
              <span className="text-th-secondary text-sm capitalize">
                {permission}
              </span>
            </div>
          </div>
        </div>

        {/* FCM Token Display (for debugging) */}
        {fcmToken && import.meta.env.MODE === 'development' && (
          <div className="bg-surface border border-subtle rounded-lg p-4">
            <h4 className="text-th-primary font-medium mb-2">FCM Token (Debug)</h4>
            <code className="text-th-secondary text-xs font-mono break-all">
              {fcmToken}
            </code>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="space-y-3">
        {permission !== 'granted' ? (
          <button
            onClick={handleEnableNotifications}
            disabled={isRegistering}
            className="w-full flex items-center justify-center space-x-2 px-6 py-3 bg-brand text-surface rounded-lg font-medium hover:bg-brand/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            {isRegistering ? (
              <>
                <LoadingSpinner size="sm" />
                <span>Enabling...</span>
              </>
            ) : (
              <>
                <Bell className="w-4 h-4" />
                <span>Enable Notifications</span>
              </>
            )}
          </button>
        ) : (
          <div className="space-y-3">
            <button
              onClick={handleTestNotification}
              disabled={isTesting}
              className="w-full flex items-center justify-center space-x-2 px-6 py-3 bg-accent text-surface rounded-lg font-medium hover:bg-accent/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {isTesting ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  <span>Sending Test...</span>
                </>
              ) : (
                <>
                  <TestTube className="w-4 h-4" />
                  <span>Send Test Notification</span>
                </>
              )}
            </button>

            <button
              onClick={handleDisableNotifications}
              className="w-full flex items-center justify-center space-x-2 px-6 py-3 bg-status-error/20 border border-status-error/30 text-status-error rounded-lg font-medium hover:bg-status-error/30 transition-all"
            >
              <BellOff className="w-4 h-4" />
              <span>Disable Notifications</span>
            </button>
          </div>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="p-4 bg-status-error/10 border border-status-error/30 rounded-lg">
          <div className="flex items-center space-x-2">
            <AlertCircle className="w-5 h-5 text-status-error" />
            <span className="text-status-error text-sm">{error}</span>
          </div>
        </div>
      )}

      {/* Test Result Display */}
      {testResult && (
        <div className={`p-4 rounded-lg ${
          testResult.includes('successfully') 
            ? 'bg-status-success/10 border border-success/30' 
            : 'bg-status-error/10 border border-status-error/30'
        }`}>
          <div className="flex items-center space-x-2">
            {testResult.includes('successfully') ? (
              <CheckCircle className="w-5 h-5 text-status-success" />
            ) : (
              <AlertCircle className="w-5 h-5 text-status-error" />
            )}
            <span className={`text-sm ${
              testResult.includes('successfully') ? 'text-status-success' : 'text-status-error'
            }`}>
              {testResult}
            </span>
          </div>
        </div>
      )}

      {/* Notification Types */}
      <div className="bg-surface border border-subtle rounded-lg p-4">
        <h4 className="text-th-primary font-medium mb-3">You'll receive notifications for:</h4>
        <div className="space-y-2 text-sm text-th-secondary">
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-brand rounded-full" />
            <span>AI generation completed</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-accent rounded-full" />
            <span>Pro requests processed</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-status-warning rounded-full" />
            <span>Low credits warning</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-status-warning rounded-full" />
            <span>Subscription updates</span>
          </div>
        </div>
      </div>
    </div>
  );
};